import { formatWithValidation } from 'next/dist/shared/lib/router/utils/format-url';
import { interpolateAs } from 'next/dist/shared/lib/router/utils/interpolate-as';
import { isLocalURL } from 'next/dist/shared/lib/router/utils/is-local-url';
import { omit } from 'next/dist/shared/lib/router/utils/omit';
import { parseRelativeUrl } from 'next/dist/shared/lib/router/utils/parse-relative-url';
import type { NextRouter } from 'next/router';

import { isCustomerChatSurface } from './customerChatSurface';

/**
 * Keeps chat.co's Google tag (gtag.js: Analytics + Ads) away from customer chat
 * pages (/embed/*, /c/*) once it has loaded in a document.
 *
 * A loaded gtag.js cannot be unloaded, and GA4's enhanced measurement reports
 * history changes on its own, so reaching a customer chat page from a document
 * where the tag runs must replace the document. installCustomerChatTagGuard
 * does that for the rest of the document's life, independent of any React
 * component staying mounted.
 */

/**
 * Thrown by the routeChangeStart backstop only (normally router.change is
 * intercepted first and nothing is thrown) to stop Next's transition before it
 * changes the URL. `cancelled` matches Next's own cancellation errors. Filtered
 * from Sentry in instrumentation-client.
 */
export const CUSTOMER_CHAT_HARD_NAVIGATION = Object.assign(
  new Error('Customer chat page: full page load instead of client-side navigation'),
  { cancelled: true }
);

const DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

let disabledInThisDocument = false;

/** True once chat.co's gtag.js has been initialized in this document. */
export const isGoogleTagInitialized = () =>
  typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Sets Google's documented per-ID opt-out flags (`ga-disable-<ID>`) and, unless
 * told not to, queues a Consent Mode update denying every signal. Whether a
 * running gtag.js honors these mid-session is not relied on: callers replace
 * the document.
 */
export const disableGoogleTag = (ids: string[], { updateConsent = true } = {}) => {
  ids.forEach((id) => {
    (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = true;
  });
  disabledInThisDocument = true;
  if (updateConsent) window.gtag?.('consent', 'update', DENIED);
};

let uninstall: (() => void) | null = null;

/**
 * Installs the guard once per document; later calls are no-ops.
 *
 * - Any client-side transition into a customer chat page (push, replace, or a
 *   redirect while handling one): set the opt-out flags, queue denied consent,
 *   and load the target as a new document (location.assign for pushState,
 *   location.replace for replaceState, so Back behaves as Next would have).
 *   Exactly one document navigation starts; later transitions are dropped.
 * - Back/forward into one: a capture-phase popstate listener runs before GA's
 *   and Next's listeners; it sets the opt-out flags only and reloads.
 * - A chat.co page restored from the back-forward cache after the guard acted
 *   reloads, so its analytics runs normally again.
 */
type DocumentNavigation = {
  assign: (url: string) => void;
  replace: (url: string) => void;
  reload: () => void;
};

const browserNavigation: DocumentNavigation = {
  assign: (url) => window.location.assign(url),
  replace: (url) => window.location.replace(url),
  reload: () => window.location.reload(),
};

/**
 * The URL Next would end up on for change(method, url, as). `as` is usually
 * final already. When it is the route template itself (e.g.
 * push({ pathname: '/embed/[id]', query: { id } }, '/embed/[id]?host=x')),
 * Next fills it in during change() (router.js `shouldInterpolate`):
 * formatWithValidation({ ...parseRelativeUrl(as), pathname: interpolateAs(...).result,
 * query: omit(hrefQuery, params) }), where the template's own search, if any,
 * wins over the rebuilt query. This makes exactly those calls with Next's own
 * helpers. Returns null when the template can't be filled, so the caller
 * defers to Next. Assumes no basePath or i18n (none configured in this app).
 */
const resolveDestination = (url: string, as: string): string | null => {
  const parsedAs = parseRelativeUrl(as);
  if (!parsedAs.pathname.includes('[')) return as;
  const parsedHref = parseRelativeUrl(url);
  if (parsedHref.pathname !== parsedAs.pathname) return null;

  const interpolated = interpolateAs(parsedHref.pathname, parsedAs.pathname, parsedHref.query);
  if (!interpolated.result) return null;

  return formatWithValidation({
    ...parsedAs,
    pathname: interpolated.result,
    query: omit(parsedHref.query, interpolated.params),
  });
};

/** Next's internal transition method (pages router), the one path every push, replace, redirect and popstate takes. */
type RouterChange = (
  method: 'pushState' | 'replaceState',
  url: string,
  as: string,
  ...rest: unknown[]
) => Promise<boolean | void>;

export const installCustomerChatTagGuard = (
  router: NextRouter,
  ids: string[],
  navigation: DocumentNavigation = browserNavigation
) => {
  if (uninstall || typeof window === 'undefined') return;

  // Once a document load toward a customer chat page has started, this document
  // is going away: no second document navigation, no further client transition.
  let leaving = false;
  const leave = (go: () => void, options?: { updateConsent?: boolean }) => {
    if (leaving) return;
    leaving = true;
    disableGoogleTag(ids, options);
    go();
  };

  // Primary: Next routes client-side transitions through router.change(method,
  // url, as) on the real instance: push/replace call it synchronously and
  // in-app redirects re-enter it with the same method (external redirects are
  // hard navigations already). Intercepting there keeps push vs replace exact
  // per transition and resolves with false, so callers see no rejection.
  // Next's own bookkeeping for the transition in progress (router.js).
  type InFlightState = { _inFlightRoute?: string; clc?: (() => void) | null };
  const instance = router as unknown as { change?: RouterChange };
  const originalChange = instance.change;
  if (typeof originalChange === 'function') {
    instance.change = function guardedChange(this: unknown, method, url, as, ...rest) {
      if (leaving) return Promise.resolve(false);
      // A non-local href is a hard navigation to that href in Next (router.js
      // change() start), whatever `as` says: leave it to Next.
      if (typeof as === 'string' && isLocalURL(url) && isCustomerChatSurface(as)) {
        const target = resolveDestination(url, as);
        // A template Next can't fill in: let Next raise its own error; the
        // routeChangeStart backstop still covers anything that proceeds.
        if (target !== null) {
          // Cancel a transition still loading, exactly as Next does when a new
          // change() starts (router.js change(): _inFlightRoute + clc), so it
          // can't write history before this document unloads.
          const state = this as InFlightState;
          if (state._inFlightRoute && state.clc) {
            router.events.emit(
              'routeChangeError',
              Object.assign(new Error('Route Cancelled'), { cancelled: true }),
              state._inFlightRoute,
              { shallow: false }
            );
            state.clc();
            state.clc = null;
          }
          leave(() =>
            method === 'replaceState' ? navigation.replace(target) : navigation.assign(target)
          );
          return Promise.resolve(false);
        }
      }
      return originalChange.call(this, method, url, as, ...rest);
    };
  }

  // Backstop, subscribed always but reached only when change() was not
  // intercepted (absent in a future Next, or a template the guard left to
  // Next): routeChangeStart is emitted before history is touched. Push vs
  // replace is unknown here, so it uses assign.
  const handleRouteChangeStart = (url: string) => {
    if (!isCustomerChatSurface(url)) return;
    leave(() => navigation.assign(url));
    throw CUSTOMER_CHAT_HARD_NAVIGATION;
  };
  const handlePopState = (event: PopStateEvent) => {
    if (!isCustomerChatSurface(window.location.pathname)) return;
    event.stopImmediatePropagation();
    // The URL has already changed: set the opt-out flags only, no gtag call.
    leave(() => navigation.reload(), { updateConsent: false });
  };
  // Only the backstop rejects (<Link> does not catch router.push); this stops
  // the browser's default reporting, and Sentry's beforeSend drops it.
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (event.reason === CUSTOMER_CHAT_HARD_NAVIGATION) event.preventDefault();
  };
  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted && disabledInThisDocument) navigation.reload();
  };

  router.events.on('routeChangeStart', handleRouteChangeStart);
  window.addEventListener('popstate', handlePopState, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  window.addEventListener('pageshow', handlePageShow);

  uninstall = () => {
    if (originalChange) instance.change = originalChange;
    router.events.off('routeChangeStart', handleRouteChangeStart);
    window.removeEventListener('popstate', handlePopState, true);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.removeEventListener('pageshow', handlePageShow);
    uninstall = null;
    disabledInThisDocument = false;
  };
};

/** Test-only: removes the guard so each test starts from a fresh document. */
export const resetCustomerChatTagGuardForTests = () => uninstall?.();
